import type { ApolloClient } from '@apollo/client';
import { gql } from '@apollo/client';
import { apolloContextForShop } from '@core/api/apolloContext';
import type { Category, SubCategory } from '../graphql/types';

/** All category rows owned by the shop (admin-created, including empty catalogs). */
export const GET_MST_CATEGORIES_BY_SHOP = gql`
  query GetMstCategoriesByShop($shopId: uuid!) {
    mst_category(
      where: { shop_id: { _eq: $shopId }, isdeleted: { _neq: true } }
      order_by: { category: asc }
    ) {
      id
      category
      image
    }
  }
`;

/** Categories linked through category_product for a shop. */
export const GET_CATEGORIES_BY_LINK = gql`
  query GetCategoriesByLink($shopId: uuid!) {
    mst_category(
      where: {
        isdeleted: { _neq: true }
        category_product: { shop_id: { _eq: $shopId } }
      }
    ) {
      id
      category
      image
    }
  }
`;

/**
 * Derive categories from visible products for shops where category_product
 * links are missing or stale.
 */
export const GET_SHOP_CATEGORIES = gql`
  query GetShopCategories($shopId: uuid!) {
    mst_product(
      where: {
        shop_id: { _eq: $shopId }
        isdeleted: { _neq: true }
        visible: { _eq: true }
        category: { _is_null: false }
        mst_category: { isdeleted: { _neq: true } }
      }
      distinct_on: category
      order_by: [{ category: asc }, { created_at: desc }]
    ) {
      category
      mst_category {
        id
        category
        image
      }
      mst_sub_category {
        id
        sub_name
        image
        updated_at
      }
    }
  }
`;

/** Subcategories keyed by parent category id (not nested under mst_category in this Hasura schema). */
export const GET_SUBCATEGORIES_BY_CATEGORY_IDS = gql`
  query GetSubcategoriesByCategoryIds($categoryIds: [uuid!]!) {
    mst_sub_category(
      where: {
        category: { _in: $categoryIds }
        isdeleted: { _neq: true }
      }
    ) {
      id
      sub_name
      image
      updated_at
      category
    }
  }
`;

/** @deprecated Use fetchShopCategories — kept for import compatibility. */
export const GET_CATEGORIES = GET_CATEGORIES_BY_LINK;

export interface ShopCategoryProductRow {
  category: string | null;
  mst_category: Category | null;
  mst_sub_category: SubCategory | null;
}

export interface GetShopCategoriesResponse {
  mst_product: ShopCategoryProductRow[];
}

export interface GetCategoriesByLinkResponse {
  mst_category: Category[];
}

export interface GetMstCategoriesByShopResponse {
  mst_category: Category[];
}

export interface GetSubcategoriesResponse {
  mst_sub_category: Array<
    SubCategory & {
      category?: string | null;
    }
  >;
}

function normalizeSubCategories(
  sub: Category['mst_sub_category'] | SubCategory | null | undefined
): SubCategory[] {
  if (Array.isArray(sub)) return sub;
  if (sub && typeof sub === 'object') return [sub as SubCategory];
  return [];
}

function mergeSubCategoryLists(a: SubCategory[], b: SubCategory[]): SubCategory[] {
  const merged = [...a];
  for (const sub of b) {
    const exists = merged.some(
      item => (sub.id && item.id === sub.id) || item.sub_name === sub.sub_name
    );
    if (!exists) merged.push(sub);
  }
  return merged;
}

export function mapProductRowsToCategories(
  rows: ShopCategoryProductRow[]
): Category[] {
  const byId = new Map<string, Category>();

  for (const row of rows) {
    const cat = row.mst_category;
    if (!cat?.id || !cat.category?.trim()) continue;

    const rowSubs = normalizeSubCategories(row.mst_sub_category);

    const existing = byId.get(cat.id);
    if (!existing) {
      byId.set(cat.id, {
        ...cat,
        mst_sub_category: rowSubs,
      });
      continue;
    }

    existing.mst_sub_category = mergeSubCategoryLists(
      existing.mst_sub_category,
      rowSubs
    );
  }

  return Array.from(byId.values()).sort((a, b) =>
    (a.category || '').localeCompare(b.category || '')
  );
}

export function mergeCategoryLists(
  linked: Category[],
  derived: Category[]
): Category[] {
  const byId = new Map<string, Category>();

  for (const cat of [...linked, ...derived]) {
    if (!cat?.id || !cat.category?.trim()) continue;
    const subs = normalizeSubCategories(cat.mst_sub_category);
    const existing = byId.get(cat.id);
    if (!existing) {
      byId.set(cat.id, { ...cat, mst_sub_category: subs });
      continue;
    }
    existing.mst_sub_category = mergeSubCategoryLists(
      existing.mst_sub_category,
      subs
    );
    if (!existing.image && cat.image) {
      existing.image = cat.image;
    }
  }

  return Array.from(byId.values()).sort((a, b) =>
    (a.category || '').localeCompare(b.category || '')
  );
}

function attachSubcategories(
  categories: Category[],
  subcategories: GetSubcategoriesResponse['mst_sub_category']
): Category[] {
  if (!subcategories.length) return categories;

  const subsByCategoryId = new Map<string, SubCategory[]>();
  for (const sub of subcategories) {
    const categoryId = sub.category;
    if (!categoryId) continue;
    const list = subsByCategoryId.get(categoryId) ?? [];
    list.push({
      id: sub.id,
      sub_name: sub.sub_name,
      image: sub.image,
      updated_at: sub.updated_at,
      gst_rate: sub.gst_rate ?? null,
      hsn_id: sub.hsn_id ?? null,
      hsn: sub.hsn ?? null,
    });
    subsByCategoryId.set(categoryId, list);
  }

  return categories.map(category => {
    if (!category.id) return category;
    const attached = subsByCategoryId.get(category.id) ?? [];
    if (!attached.length) return category;
    return {
      ...category,
      mst_sub_category: mergeSubCategoryLists(
        normalizeSubCategories(category.mst_sub_category),
        attached
      ),
    };
  });
}

export async function fetchShopCategories(
  client: ApolloClient,
  shopId: string,
  fetchPolicy: 'cache-first' | 'network-only' = 'cache-first'
): Promise<Category[]> {
  const context = apolloContextForShop(shopId);
  const [directRes, linkedRes, derivedRes] = await Promise.allSettled([
    client.query<GetMstCategoriesByShopResponse>({
      query: GET_MST_CATEGORIES_BY_SHOP,
      variables: { shopId },
      fetchPolicy,
      context,
    }),
    client.query<GetCategoriesByLinkResponse>({
      query: GET_CATEGORIES_BY_LINK,
      variables: { shopId },
      fetchPolicy,
      context,
    }),
    client.query<GetShopCategoriesResponse>({
      query: GET_SHOP_CATEGORIES,
      variables: { shopId },
      fetchPolicy,
      context,
    }),
  ]);

  if (directRes.status === 'rejected') {
    console.warn(
      `[categories] direct shop query failed for shop "${shopId}":`,
      directRes.reason
    );
  }
  if (linkedRes.status === 'rejected') {
    console.warn(
      `[categories] linked query failed for shop "${shopId}":`,
      linkedRes.reason
    );
  }
  if (derivedRes.status === 'rejected') {
    console.warn(
      `[categories] derived query failed for shop "${shopId}":`,
      derivedRes.reason
    );
  }

  const direct =
    directRes.status === 'fulfilled'
      ? directRes.value.data?.mst_category ?? []
      : [];
  const linked =
    linkedRes.status === 'fulfilled'
      ? linkedRes.value.data?.mst_category ?? []
      : [];
  const derived =
    derivedRes.status === 'fulfilled'
      ? mapProductRowsToCategories(derivedRes.value.data?.mst_product ?? [])
      : [];

  const merged = mergeCategoryLists(
    mergeCategoryLists(direct, linked),
    derived
  );
  const categoryIds = merged.map(category => category.id).filter(Boolean) as string[];
  if (!categoryIds.length) {
    return merged;
  }

  try {
    const subRes = await client.query<GetSubcategoriesResponse>({
      query: GET_SUBCATEGORIES_BY_CATEGORY_IDS,
      variables: { categoryIds },
      fetchPolicy,
      context,
    });
    return attachSubcategories(merged, subRes.data?.mst_sub_category ?? []);
  } catch (error) {
    console.warn(
      `[categories] subcategory query failed for shop "${shopId}":`,
      error
    );
    return merged;
  }
}
